import { useState, useEffect } from "react";
import { DemoHint } from "@/components/demo/DemoHint";
import { RotateCcw, ChevronRight, ChevronDown, Loader2, Trash2 } from "lucide-react";
import { PostLoadCard } from "@/components/post-load/PostLoadCard";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/contexts/AuthContext";

export interface TruckTemplate {
  id: string;
  name: string;
  origin: string;
  destination: string;
  equipment_type: string;
  length_ft: number;
  max_weight_lbs: number;
  load_type: string;
  open_to_anywhere: boolean;
  usage_count: number;
  last_used_at: string | null;
}

interface Props {
  onUseTemplate: (template: TruckTemplate) => void;
}

export default function RepeatTruckTemplates({ onUseTemplate }: Props) {
  const { user } = useAuth();
  const { toast } = useToast();
  const [open, setOpen] = useState(true);
  const [templates, setTemplates] = useState<TruckTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const handleDelete = async (e: React.MouseEvent, t: TruckTemplate) => {
    e.stopPropagation();
    if (!confirm(`Delete template "${t.origin || "—"} → ${t.open_to_anywhere ? "Anywhere" : (t.destination || "—")}"?`)) return;
    setDeletingId(t.id);
    const { error } = await supabase.from("truck_templates").delete().eq("id", t.id);
    setDeletingId(null);
    if (error) {
      toast({ title: "Failed to delete template", description: error.message, variant: "destructive" });
      return;
    }
    setTemplates((prev) => prev.filter((p) => p.id !== t.id));
    toast({ title: "Template deleted." });
  };

  useEffect(() => {
    if (!user) { setLoading(false); return; }

    const fetchTemplates = async () => {
      try {
        // Resolve company for current user (truck_templates are company-scoped)
        const { data: cu } = await supabase
          .from("company_users")
          .select("company_id")
          .eq("user_id", user.id)
          .eq("is_active", true)
          .maybeSingle();

        if (!cu?.company_id) { setTemplates([]); return; }

        const { data, error } = await supabase
          .from("truck_templates")
          .select("*")
          .eq("company_id", cu.company_id)
          .order("usage_count", { ascending: false })
          .order("last_used_at", { ascending: false, nullsFirst: false })
          .limit(10);

        if (error) { console.error("Truck template fetch error:", error); return; }
        setTemplates((data as TruckTemplate[]) || []);
      } catch (err) {
        console.error("RepeatTruckTemplates error:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchTemplates();
  }, [user]);

  return (
    <PostLoadCard>
      <button onClick={() => setOpen(!open)} className="flex items-center justify-between w-full px-4 py-3">
        <div className="flex items-center gap-2">
          <RotateCcw className="h-4 w-4" style={{ color: "hsl(var(--brand-teal-deep))" }} />
          <DemoHint hintKey="section.repeatTemplates" side="right">
            <h4 className="text-sm font-semibold text-foreground">Repeat Truck Templates</h4>
          </DemoHint>
          {templates.length > 0 && (
            <span className="text-[10px] font-medium px-1.5 py-0.5 rounded-full bg-muted text-muted-foreground">
              {templates.length}
            </span>
          )}
        </div>
        {open ? <ChevronDown className="h-4 w-4 text-muted-foreground" /> : <ChevronRight className="h-4 w-4 text-muted-foreground" />}
      </button>

      <div className="overflow-hidden transition-all duration-200" style={{ maxHeight: open ? 400 : 0, opacity: open ? 1 : 0 }}>
        <div className="px-4 pb-3 space-y-0">
          {loading ? (
            <div className="flex items-center justify-center py-6 text-muted-foreground gap-2">
              <Loader2 className="h-4 w-4 animate-spin" />
              <span className="text-xs">Loading templates…</span>
            </div>
          ) : templates.length === 0 ? (
            <div className="py-6 text-center text-xs text-muted-foreground">
              No truck templates yet. Save one when posting a truck.
            </div>
          ) : (
            templates.map((t) => (
              <div key={t.id} className="flex items-center justify-between py-2.5 border-b border-border last:border-0">
                <div className="flex items-center gap-2 text-xs text-foreground min-w-0">
                  <span className="text-muted-foreground">▸</span>
                  <span className="font-semibold truncate">{t.origin || "—"}</span>
                  <span className="text-muted-foreground">→</span>
                  <span className="font-semibold truncate">{t.open_to_anywhere ? "Anywhere" : (t.destination || "—")}</span>
                  <span className="text-muted-foreground">– {t.equipment_type || "—"}</span>
                </div>
                <div className="flex items-center gap-3 flex-shrink-0">
                  {t.usage_count > 0 && (
                    <span className="text-[11px] text-muted-foreground">{t.usage_count}x used</span>
                  )}
                  <button
                    onClick={() => onUseTemplate(t)}
                    className="text-[11px] font-semibold px-2.5 py-1 rounded-md border border-border bg-background hover:bg-muted transition-colors text-foreground"
                  >
                    Use Template
                  </button>
                  <button
                    onClick={(e) => handleDelete(e, t)}
                    disabled={deletingId === t.id}
                    aria-label="Delete template"
                    title="Delete template"
                    className="p-1 rounded-md text-muted-foreground hover:text-destructive hover:bg-muted transition-colors disabled:opacity-50"
                  >
                    {deletingId === t.id ? (
                      <Loader2 className="h-3.5 w-3.5 animate-spin" />
                    ) : (
                      <Trash2 className="h-3.5 w-3.5" />
                    )}
                  </button>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </PostLoadCard>
  );
}
